import { useNavigate } from 'react-router-dom';
import { Mail, FileText, Send, Paperclip } from 'lucide-react';
import type { CrmConversation } from '../../types';
import { SOURCE_LABEL, STATUS_COLOR, STATUS_LABEL, relativeTime, senderLabel, waitingLabel } from '../../lib/crmInbox';
import EmptyState from '../EmptyState';

const SOURCE_ICON = {
  email: <Mail className="w-3.5 h-3.5" />,
  form: <FileText className="w-3.5 h-3.5" />,
  manual: <Send className="w-3.5 h-3.5" />,
};

const TONE_CLASS = { ok: 'text-muted', warn: 'text-warn', err: 'text-err' };

interface Props {
  conversations: CrmConversation[];
  empty?: React.ReactNode;
  /** Hide the owner column when every row has the same one (contact page). */
  compact?: boolean;
}

/** The inbox rows. Unread threads are bold; "Waiting Xh" says how long
 *  they have been waiting on us and turns red after a day. */
export default function ConversationList({ conversations, empty, compact }: Props) {
  const navigate = useNavigate();
  if (conversations.length === 0) {
    return <EmptyState icon={<Mail className="w-8 h-8" />}>{empty ?? 'No conversations here.'}</EmptyState>;
  }
  return (
    <div className="card" style={{ padding: 0 }}>
      <ul className="divide-y divide-line">
        {conversations.map(c => {
          const waiting = waitingLabel(c);
          return (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => navigate(`/crm/inbox/${c.id}`)}
                className="w-full text-left px-4 py-3 hover:bg-row-odd transition-colors flex items-start gap-3"
              >
                <span
                  className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: c.isUnread ? 'var(--scpa-primary)' : 'transparent' }}
                  aria-label={c.isUnread ? 'Unread' : undefined}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm truncate max-w-[16rem] ${c.isUnread ? 'font-bold text-fg' : 'font-medium text-fg'}`}>{senderLabel(c)}</span>
                    {c.fromName && <span className="text-xs text-muted truncate max-w-[14rem] hidden sm:inline">{c.fromEmail}</span>}
                    {c.organization && <span className="text-xs text-muted hidden md:inline">· {c.organization.name}</span>}
                    <span className="ml-auto text-xs text-muted whitespace-nowrap">{relativeTime(c.lastMessageAt)}</span>
                  </div>
                  <p className={`text-sm truncate ${c.isUnread ? 'font-semibold text-fg' : 'text-fg'}`}>
                    <span className="font-mono text-[11px] text-muted mr-1.5">CRM-{c.number}</span>{c.subject}
                  </p>
                  <p className="text-xs text-muted truncate">{c.preview}</p>
                  <div className="flex items-center gap-3 mt-1 text-[11px] text-muted flex-wrap">
                    <span className="inline-flex items-center gap-1" title={`Source: ${SOURCE_LABEL[c.source]}`}>{SOURCE_ICON[c.source]} {SOURCE_LABEL[c.source]}</span>
                    <span style={{ color: STATUS_COLOR[c.status], fontWeight: 700 }}>{STATUS_LABEL[c.status]}</span>
                    {waiting && <span className={`font-semibold ${TONE_CLASS[waiting.tone]}`}>{waiting.text}</span>}
                    {c.messageCount > 1 && <span>{c.messageCount} messages</span>}
                    {c.deal && <span className="inline-flex items-center gap-1"><Paperclip className="w-3 h-3" /> {c.deal.title}</span>}
                    {!compact && (c.owner ? <span>Owner: {c.owner.name}</span> : <span className="text-warn">Unassigned</span>)}
                    {c.tags?.length > 0 && c.tags.map(t => <span key={t} className="badge bg-primary-muted text-primary">{t}</span>)}
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
